// 代理商登录页 —— 镜像 customer /login（仅登录，无自助注册）
// principal / password / 动态验证码（2026-10-31 口径变更：已绑定 TOTP 用户必填，未绑定可空）
import { mapApiError } from '@/lib/apiErrorMessages';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { Building2, User, Lock, LogIn, KeyRound } from 'lucide-react';
import { login, USE_MOCK } from '@/api/auth';

export default function LoginPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [principal, setPrincipal] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  /** 登录后目标：优先回到来源页（守卫记录的 from），否则进 Dashboard */
  const goAfterLogin = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from || '/agent/dashboard', { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!principal.trim() || !password.trim()) {
      toast.error('请输入账号和密码');
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(principal.trim(), password, otp.trim() || undefined);
      if (!res.ok) {
        toast.error(mapApiError(res.code, res.error));
        if (res.code === '3410') {
          // 已绑定 TOTP 用户验证码缺失/错误（MFA_OTP_REQUIRED / MFA_OTP_INVALID）：
          // 表单内增强提示并聚焦验证码框（不覆盖 toast 既有 mapApiError 行为）
          setOtpError('动态验证码缺失或错误，请输入 Google Authenticator 的 6 位验证码');
          otpInputRef.current?.focus();
        } else {
          setOtpError('');
        }
        return;
      }
      toast.success(USE_MOCK ? '登录成功（mock）' : '欢迎回来');
      const me = (await import('@/api/client')).loadSession()?.me;
      if (me && me.emailVerified === false) {
        navigate('/verify-email', { replace: true });
        return;
      }
      goAfterLogin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900">Allinks</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">代理商展业后台</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">代理商登录</h1>
          <p className="text-sm text-slate-500 text-center mb-6">登录后管理名下商户与价差结算</p>

          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">账号</label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-slate-800 text-sm placeholder:text-slate-400 focus:outline-none focus:border-brand-500 focus:bg-white transition-colors"
                  placeholder="agent01"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">密码</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-slate-800 text-sm placeholder:text-slate-400 focus:outline-none focus:border-brand-500 focus:bg-white transition-colors"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Google 动态验证码（2FA）</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-slate-800 text-sm placeholder:text-slate-400 focus:outline-none focus:border-brand-500 focus:bg-white transition-colors"
                  placeholder="已开启 2FA 请输入 6 位动态验证码"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoComplete="one-time-code"
                />
              </div>
              {otpError && <p className="mt-1.5 text-xs text-red-600">{otpError}</p>}
            </div>
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-60"
              disabled={submitting}
            >
              <LogIn className="w-4 h-4" />
              {submitting ? '登录中…' : '登录'}
            </button>
          </form>

          {USE_MOCK && (
            <p className="mt-4 text-[11px] text-slate-400 text-center">
              Seed: agent01（真联调时使用平台分配账号）
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
